import { cloneElement, type ReactNode, useState } from "react";
import ModalBox from "src/components/ModalBox";
import TextInput from "src/components/TextInput";
import { Button, type ButtonProps } from "src/components/ui/button";
import { RegularText } from "src/components/v1/Text";
import { useMutation } from "src/lib";
import { cn } from "src/utilities/shadcnUtils";

import type { PopupProps } from "../Popup";

/** Legacy v1-Button style names kept for ModalAction caller compatibility. */
export type LegacyButtonType =
  | "primary"
  | "secondary"
  | "urgent"
  | "inverse"
  | "inversePrimary"
  | "ghost"
  | "ghostUrgent";

export const VARIANT_BY_LEGACY_TYPE: Record<LegacyButtonType, ButtonProps["variant"]> = {
  primary: "outline:green",
  secondary: "warning",
  urgent: "outline:red",
  inverse: "outline",
  inversePrimary: "outline:green",
  ghost: "ghost",
  ghostUrgent: "ghost:red",
};

export const SIZE_BY_LEGACY_SIZE: Record<"standard" | "small", ButtonProps["size"]> = {
  standard: "default",
  small: "sm",
};

export interface ModalAction {
  promptInput?: string;
  promptRequired?: boolean;
  text: string;
  type?: LegacyButtonType;
  action?: (promptInput?: string) => any | Promise<any>;
  submit?: boolean;
  secondary?: boolean;
  close?: boolean;
  onClose?: () => void;
  disabled?: boolean;
  loading?: boolean;
  size?: "standard" | "small";
  autoFocus?: boolean;
}

export interface ModalProps extends Omit<PopupProps, "children"> {
  message: ReactNode;
  header?: ReactNode;
  prompt?: string;
  /** Initial text in the prompt input (e.g. a suggested reason to edit). */
  defaultPromptValue?: string;
  actions?: ModalAction[];
  onClose?: () => void;
  children?: ReactNode;
  zIndex?: number;
}

const ActionButton = ({
  text,
  action,
  type,
  onClose,
  submit,
  disabled,
  loading,
  promptInput,
  size,
  autoFocus,
}: ModalAction) => {
  const handleClickAction = useMutation({
    mutationFn: async (_e: any) => {
      await action?.(promptInput);
      onClose?.();
    },
  });

  return (
    <Button
      className="ml-4"
      onClick={handleClickAction.mutate}
      variant={VARIANT_BY_LEGACY_TYPE[type ?? "primary"]}
      type={submit ? "submit" : "button"}
      pending={loading || handleClickAction.isPending}
      disabled={disabled}
      size={SIZE_BY_LEGACY_SIZE[size ?? "standard"]}
      autoFocus={autoFocus}
    >
      {text}
    </Button>
  );
};

export const ModalInnards = ({
  onClose,
  message,
  header,
  actions,
  prompt,
  defaultPromptValue,
  children,
  className,
}: ModalProps) => {
  const [modalInput, setModalInput] = useState(defaultPromptValue ?? "");
  return (
    <div className={cn("px-8 py-6", className)}>
      {header ? <p className="text-2xl font-bold">{header}</p> : null}
      <RegularText className="text-base" paragraph>
        {/* @ts-ignore */}
        {!message || typeof message === "string" ? message : cloneElement(message, { onClose })}
      </RegularText>
      {prompt ? (
        <TextInput
          containerClassName="mt-4"
          label={prompt}
          defaultValue={modalInput}
          onChange={setModalInput}
        />
      ) : null}
      {children}
      <div className={cn("flex flex-row justify-end mt-3", prompt && "mt-1.5")}>
        {actions?.map((action, index) => (
          <ActionButton
            autoFocus={action.autoFocus}
            key={index}
            action={action.action}
            text={action.text}
            type={action.type}
            size={action.size}
            onClose={action.close === false ? undefined : onClose}
            promptInput={modalInput}
            disabled={action.disabled || (action.promptRequired && !modalInput?.trim()?.length)}
            loading={action.loading}
          />
        ))}
        {!actions && <ActionButton text="Okay" onClose={onClose} autoFocus />}
      </div>
    </div>
  );
};

const Modal = ({
  message,
  header,
  actions,
  prompt,
  defaultPromptValue,
  children,
  zIndex,
  ...props
}: ModalProps) => (
  <ModalBox closeButton={false} {...props} zIndex={zIndex}>
    <ModalInnards
      header={header}
      prompt={prompt}
      defaultPromptValue={defaultPromptValue}
      message={message}
      actions={actions}
    >
      {children}
    </ModalInnards>
  </ModalBox>
);

export default Modal;
